// Client-side "auto-crop": scans a frame image for its subject's bounding box (transparent-
// background PNGs use the alpha channel; opaque images fall back to comparing against the
// dominant corner-sampled background color) so multiple uploaded frames line up on the same
// canvas without the GM having to crop each one by hand. Returns a square, padded
// react-easy-crop-style `{x, y, width, height}` box in ORIGINAL image pixel coordinates —
// feed it straight into `initialCroppedAreaPixels` (for manual fine-tuning) or
// `getCroppedImageBlob` (to use as-is).
export async function detectSubjectBounds(imageSrc) {
  const img = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0);
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);

  const hasAlpha = detectHasTransparency(data);
  const bg = hasAlpha ? null : sampleCornerColor(data, width, height);

  let minX = width, minY = height, maxX = 0, maxY = 0;
  let found = false;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4;
      const isSubject = hasAlpha ? data[i + 3] > 20 : colorDistance(data, i, bg) > 40;
      if (isSubject) {
        found = true;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (!found) return { x: 0, y: 0, width, height };

  // Pad ~12% and square it off so every frame in a sequence crops to the same shape.
  const boxW = maxX - minX;
  const boxH = maxY - minY;
  const pad = Math.max(boxW, boxH) * 0.12;
  let x = Math.max(0, minX - pad);
  let y = Math.max(0, minY - pad);
  let size = Math.max(boxW, boxH) + pad * 2;
  if (x + size > width) size = width - x;
  if (y + size > height) size = height - y;
  size = Math.min(size, width - x, height - y);
  return { x, y, width: size, height: size };
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function detectHasTransparency(data) {
  for (let i = 3; i < data.length; i += 4 * 37) {
    if (data[i] < 250) return true;
  }
  return false;
}

function sampleCornerColor(data, width, height) {
  const i = 0; // top-left pixel
  return [data[i], data[i + 1], data[i + 2]];
}

function colorDistance(data, i, bg) {
  const dr = data[i] - bg[0];
  const dg = data[i + 1] - bg[1];
  const db = data[i + 2] - bg[2];
  return Math.sqrt(dr * dr + dg * dg + db * db);
}
